import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GetJson } from '@awp/helpers/fetchReact';
import { REST_API, STATE_STATUS } from '@awp/constants';
import { TreeSelectHelper } from '@components/Common/Helpers/TreeSelectHelper';

const initialState = {
  status: {
    hazardForecastAreas: STATE_STATUS.IDLE,
    hazardForecastDropDown: STATE_STATUS.IDLE,
    hazardLevelDropDown: STATE_STATUS.IDLE,
    hazardTrendDropDown: STATE_STATUS.IDLE,
  },
  hazardForecastAreas: [],
  hazardForecastDropDown: [],
  hazardLevelDropDown: [],
  hazardTrendDropDown: [],
  editing: null,
  analysis: null,
  weatherForecast: null,
  weatherObservation: null,
  weatherForecastExternalObjects: [],
};

export const selectAllHazardForecastAreas = (state) => state.hazardForecast.hazardForecastAreas;
export const selectAllHazardForecastDropDown = (state) => state.hazardForecast.hazardForecastDropDown;
export const selectAllHazardForecastLevelDropDown = (state) => state.hazardForecast.hazardLevelDropDown;
export const selectAllHazardTrendDropDown = (state) => state.hazardForecast.hazardTrendDropDown;
export const isEditing = (state) => state.hazardForecast.editing;
export const currentAnalysis = (state) => state.hazardForecast.analysis;
export const currentWeatherForecast = (state) => state.hazardForecast.weatherForecast;
export const currentWeatherObservation = (state) => state.hazardForecast.weatherObservation;
export const currentWeatherForecastExternalObjects = (state) => state.hazardForecast.weatherForecastExternalObjects;

export const selectAllActiveAvalancheAreaDropDowns = (state) => {
  const hazardForecastDropDown = state.hazardForecast.hazardForecastDropDown;
  const avalancheAreas = hazardForecastDropDown?.find((hf) => {
    return hf.groupName === 'Avalanche Areas';
  })?.children;

  return TreeSelectHelper.filterOutDisabled(avalancheAreas);
};

const setProgramId = (node, programId) => {
  node.programId = programId;
  if (node.children && node.children.length > 0) {
    node.children.forEach((child) => setProgramId(child, programId));
  }
};

export const fetchHazardForecastAreas = createAsyncThunk('hazardForecast/fetchHazardForecastAreas', async () => {
  let url = REST_API.HAZARD_FORECAST_AREAS;
  const res = await GetJson(url);
  return await res.json();
});

export const fetchHazardForecastDropDown = createAsyncThunk('hazardForecast/fetchHazardForecastDropDown', async () => {
  let url = REST_API.HAZARD_FORECAST + 'dropdownlist';
  const res = await GetJson(url);
  const json = await res.json();
  json.forEach((x) => {
    x.children.forEach((y) => {
      setProgramId(y, y.parentId);
    });
  });
  return json;
});

export const fetchHazardForecastLevelDropDown = createAsyncThunk(
  'hazardForecast/fetchHazardForecastLevelDropDown',
  async () => {
    let url = REST_API.HAZARD_FORECAST + 'leveldropdownlist';
    const res = await GetJson(url);
    return await res.json();
  },
);

export const fetchHazardTrendDropDown = createAsyncThunk('hazardForecast/fetchHazardTrendDropDown', async () => {
  let url = REST_API.HAZARD_FORECAST + 'trenddropdownlist';
  const res = await GetJson(url);
  return await res.json();
});
/**
 * SELECTORS
 */

export const hazardForecastSlice = createSlice({
  name: 'hazardForecast',
  initialState,
  reducers: {
    setEditing(state, action) {
      //editing: sot+id or sp+id
      return { ...state, editing: action.payload };
    },
    setAnalysis(state, action) {
      return { ...state, analysis: action.payload };
    },
    setWeatherForecast(state, action) {
      return { ...state, weatherForecast: action.payload };
    },
    setWeatherObservation(state, action) {
      return { ...state, weatherObservation: action.payload };
    },
    setWeatherForecastExternalObjects(state, action) {
      return { ...state, weatherForecastExternalObjects: action.payload };
    },
  },
  extraReducers: {
    [fetchHazardForecastAreas.pending]: (state) => {
      state.status.hazardForecastAreas = STATE_STATUS.LOADING;
    },
    [fetchHazardForecastAreas.fulfilled]: (state, action) => {
      state.status.hazardForecastAreas = STATE_STATUS.SUCCESS;
      state.hazardForecastAreas = action.payload;
    },
    [fetchHazardForecastAreas.rejected]: (state, action) => {
      state.status.hazardForecastAreas = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },

    [fetchHazardForecastDropDown.pending]: (state) => {
      state.status.hazardForecastDropDown = STATE_STATUS.LOADING;
    },
    [fetchHazardForecastDropDown.fulfilled]: (state, action) => {
      state.status.hazardForecastDropDown = STATE_STATUS.SUCCESS;
      state.hazardForecastDropDown = action.payload;
    },
    [fetchHazardForecastDropDown.rejected]: (state, action) => {
      state.status.hazardForecastDropDown = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },

    [fetchHazardForecastLevelDropDown.pending]: (state) => {
      state.status.hazardLevelDropDown = STATE_STATUS.LOADING;
    },
    [fetchHazardForecastLevelDropDown.fulfilled]: (state, action) => {
      state.status.hazardLevelDropDown = STATE_STATUS.SUCCESS;
      state.hazardLevelDropDown = action.payload;
    },
    [fetchHazardForecastLevelDropDown.rejected]: (state, action) => {
      state.status.hazardLevelDropDown = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },

    [fetchHazardTrendDropDown.pending]: (state) => {
      state.status.hazardTrendDropDown = STATE_STATUS.LOADING;
    },
    [fetchHazardTrendDropDown.fulfilled]: (state, action) => {
      state.status.hazardTrendDropDown = STATE_STATUS.SUCCESS;
      state.hazardTrendDropDown = action.payload;
    },
    [fetchHazardTrendDropDown.rejected]: (state, action) => {
      state.status.hazardTrendDropDown = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
  },
});

export const { setEditing, setAnalysis, setWeatherObservation, setWeatherForecastExternalObjects } =
  hazardForecastSlice.actions;
